import { Galeria } from "@/galeria/Galeria";
import { router } from "@/main";
import { GET } from "@/state/api";
import { DateZod, TimeZod } from "@/state/types";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

const SearchZod = z.union([
  z.intersection(
    z.union([
      z.object({
        t: z.number(),
        d: DateZod,
        f: z.number(),
        h: TimeZod.optional(),
      }),
      z.object({ subida_id: z.coerce.number() }),
    ]),
    z.object({ s: z.number().optional() })
  ),
  z.object({ foto_id: z.number() }),
]);

export const Route = createFileRoute("/_app/buscador")({
  component: Buscador,
  validateSearch: SearchZod,
  // Navegar entre fotos NUNCA recarga la galeria, a cualquier tiempo transcurrido:
  //  - shouldReload:false  -> ninguna navegacion recarga el loader.
  //  - staleTime:Infinity  -> los datos nunca se marcan viejos por tiempo.
  // Antes staleTime:120_000 hacia que, tras una pausa > 2 min, la navegacion revalidara el
  // loader y re-pidiera /buscar (galeria entera). En el Atom lento eso encadenaba refetches
  // (cada tecla volvia a encontrar datos "viejos") y la navegacion quedaba clavada.
  // Las fotos nuevas del dia entran cuando se hace una busqueda nueva (cambian t/d/f -> otras
  // deps -> refetch deliberado), no en medio de la navegacion.
  staleTime: Infinity,
  shouldReload: false,
  // Excluir 's' (indice de foto) de las deps del loader.
  loaderDeps: ({ search }) => {
    const q = search as z.infer<typeof SearchZod>;
    return "foto_id" in q
      ? { foto_id: q.foto_id }
      : "subida_id" in q
        ? { subida_id: q.subida_id }
        : { t: q.t, d: q.d, f: q.f };
  },
  loader: async (params) => {
    // this should be inferable
    const search = params.location.search as z.infer<typeof SearchZod>;
    return (
      await GET("/buscar", {
        q:
          "subida_id" in search
            ? { "buscador[subida_id]": search.subida_id }
            : "foto_id" in search
              ? { foto: search.foto_id }
              : {
                  "buscador[temporada_id]": search.t,
                  "buscador[fecha]": search.d,
                  "buscador[fotografo]": search.f,
                },
      })
    ).galeria;
  },
});

function Buscador() {
  const { fotos, posicion } = Route.useLoaderData();
  const search = Route.useSearch();
  //  const startIdx = h ? fotos.findIndex((f) => f.h >= h) : undefined;

  return (
    <Galeria
      fotos={fotos}
      selected={posicion ?? ("s" in search ? search.s : undefined)}
      onSelected={(idx) =>
        router.navigate({
          to: "/buscador",
          search: { ...search, s: idx },
          replace: true,
        })
      }
    />
  );
}
